import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import PageHeader from '../../cpanel/PageHeader';

const CargaCotizacionArtPage = () => {
    const navigate = useNavigate();
    
    // Consumimos el estado de carga y permisos de la ACL
    const { can, isFullUser, loadingAuth } = usePermissions();

    // Módulos específicos para el área de ART
    const modules = [
        {
            title: "Cotizaciones ART",
            description: "Accede al tablero principal para visualizar, filtrar y gestionar el historial de todas las cotizaciones.",
            category: "Comercial",
            icon: (
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
            ),
            path: "/Cotizaciones-art",
            chipBg: "bg-blue-50 text-blue-700 border-blue-100",
            hoverBorder: "hover:border-blue-300"
        },
        {
            title: "Gestión de Bloqueos ART",
            description: "Administra las fechas de bloqueo de las compañías para evitar conflictos de comercialización en el sistema.",
            category: "Seguridad",
            icon: (
                <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
            ),
            path: "/bloqueos-art",
            chipBg: "bg-rose-50 text-rose-700 border-rose-100",
            hoverBorder: "hover:border-rose-300"
        },
        {
            title: "Gestión de Aumentos",
            description: "Controla y realiza el seguimiento de las solicitudes de aumento de tarifa para los contratos vigentes.",
            category: "Tarifas",
            icon: (
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
            ),
            path: "/aumentos-art",
            chipBg: "bg-emerald-50 text-emerald-700 border-emerald-100",
            hoverBorder: "hover:border-emerald-300"
        },
        {
            title: "Importador de Aumentos ART",
            description: "Procesamiento e importación masiva de endosos de aumentos de tarifa.",
            category: "Archivos",
            icon: (
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
            ),
            path: "/importacion-aumentos-art",
            chipBg: "bg-amber-50 text-amber-700 border-amber-100",
            hoverBorder: "hover:border-amber-300"
        }
    ];

    // --- FILTRADO ADAPTATIVO ACL ---
    const filteredModules = useMemo(() => {
        return modules.filter(module => {
            if (!isFullUser) {
                return module.path === "/Cotizaciones-art";
            }
            if (module.path === "/aumentos-art") return can('VER_GESTION_AUMENTOS');
            if (module.path === "/importacion-aumentos-art") return can('VER_IMPORTADOR_AUMENTOS');
            
            return true;
        });
    }, [isFullUser, can]);

    // Spinner de carga con identidad del sistema
    if (loadingAuth) {
        return (
            <div className="min-h-screen bg-[#f3f4f8] flex items-center justify-center font-sans text-left">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-slate-200 border-t-amber-600 rounded-full animate-spin" />
                    <span className="text-slate-500 text-xs font-bold tracking-wider animate-pulse">Sincronizando permisos de ART...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f3f4f8] font-sans text-left pb-16 w-full antialiased flex flex-col">
            
            {/* HEADER SUPERIOR UNIFICADO */}
            <PageHeader 
                title="Gestión de Riesgos del Trabajo"
                subtitle={
                    isFullUser 
                        ? "Administración integral de cotizaciones, aumentos y bloqueos de ART" 
                        : "Consulta de cotizaciones históricas y vigentes registradas"
                }
            />

            {/* CONTENIDO PRINCIPAL DIRECTO EN EL FONDO */}
            <div className="w-full px-24 pt-14 max-w-[1600px] mx-auto space-y-6 flex-grow">
                
                {/* CABECERA DE SECCIÓN (SIN CAJA BLANCA) */}
                <div className="flex items-center justify-between border-b border-slate-200/80 pb-4">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                            Módulos Operativos de ART
                        </h2>
                        <p className="text-sm text-slate-400 font-medium mt-1">
                            Selecciona una funcionalidad para gestionar el flujo comercial de Riesgos del Trabajo
                        </p>
                    </div>
                    <span className="bg-amber-50 text-amber-700 text-[10px] font-black px-3 py-1 rounded-full border border-amber-200 uppercase tracking-wider">
                        {filteredModules.length} Módulos
                    </span>
                </div>

                {/* GRID DE CARDS DIRECTAS SOBRE EL FONDO */}
                <main className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filteredModules.map((item, index) => (
                        <div 
                            key={index}
                            onClick={() => navigate(item.path)}
                            className={`group bg-white border border-slate-200/80 ${item.hoverBorder} rounded-2xl p-5 transition-all duration-200 flex flex-col justify-between h-full shadow-2xs hover:shadow-xl hover:-translate-y-1 cursor-pointer`}
                        >
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 group-hover:bg-white transition-colors">
                                        {item.icon}
                                    </div>
                                    <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-md border ${item.chipBg}`}>
                                        {item.category}
                                    </span>
                                </div>

                                <h4 className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors tracking-tight">
                                    {item.title}
                                </h4>

                                <p className="text-slate-400 text-xs leading-relaxed font-medium mt-1.5">
                                    {item.description}
                                </p>
                            </div>

                            <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-slate-400 group-hover:text-blue-600 transition-colors">
                                <span className="text-[11px] uppercase tracking-wider font-extrabold">Ingresar</span>
                                <span className="transform group-hover:translate-x-1.5 transition-transform text-sm">→</span>
                            </div>
                        </div>
                    ))}
                </main>

            </div>

            {/* FOOTER */}
            <footer className="mt-16 pt-6 border-t border-slate-200/80 text-center select-none shrink-0">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                    &copy; {new Date().getFullYear()} IB BROKERS SYSTEM &bull; INTELLIGENT INSURANCE MANAGEMENT &bull; v2.5
                </p>
            </footer>
        </div>
    );
};

export default CargaCotizacionArtPage;